import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface MonthlyData {
  month: string;
  spending: number;
  income: number;
}

interface SpendingMiniBoxProps {
  monthlyData: MonthlyData[];
}

export const SpendingMiniBox = ({ monthlyData }: SpendingMiniBoxProps) => {
  // Early return if no data
  if (!monthlyData || monthlyData.length === 0) {
    return (
      <div className="bg-white rounded-xl p-4 border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51]">
        <div className="text-[#221A51]/60 text-sm mb-3">Income vs Spending</div>
        <div className="h-32 flex items-center justify-center text-[#221A51]/60">
          No data available
        </div>
      </div>
    );
  }

  // Ensure all data points are valid numbers
  const validData = monthlyData.map(d => ({
    month: d.month,
    income: typeof d.income === 'number' ? d.income : 0,
    spending: typeof d.spending === 'number' ? d.spending : 0
  }));

  const chartData = {
    labels: validData.map(d => d.month),
    datasets: [
      {
        label: 'Income',
        data: validData.map(d => d.income),
        borderColor: '#9BFFA9',
        backgroundColor: '#9BFFA9',
        tension: 0.4,
        pointRadius: 0,
      },
      {
        label: 'Spending',
        data: validData.map(d => d.spending),
        borderColor: '#9B86EA',
        backgroundColor: '#9B86EA',
        tension: 0.4,
        pointRadius: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
  };

  return (
    <div className="bg-white rounded-xl p-4 border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51]">
      <div className="text-[#221A51]/60 text-sm mb-3">Income vs Spending</div>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="h-32"
      >
        <Line data={chartData} options={chartOptions} />
      </motion.div>
      <div className="flex justify-between mt-2 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 rounded-full bg-[#9BFFA9]" />
          <span className="text-[#221A51]">Income</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 rounded-full bg-[#9B86EA]" />
          <span className="text-[#221A51]">Spending</span>
        </div>
      </div>
    </div>
  );
};
